/**
 * MODULE: home/LiveIntelligenceSection.jsx
 * PURPOSE: Homepage "Live Intelligence" story carousel (paginated grid) +
 *          the shared LiveFeedPanel/SignalCard used by the map's side panel.
 *          Extracted from monolithic pages/Home.jsx (Phase 4 split).
 * PUBLIC API: <LiveIntelligenceSection stories={[]} country={obj} />,
 *   LiveFeedPanel (also used by home/LiveIntelligenceMapSection.jsx)
 * DEPENDS ON: components/dnh/atoms.ConfidenceBadge
 * CALLED BY: pages/Home.jsx, home/LiveIntelligenceMapSection.jsx (LiveFeedPanel)
 * LAST REVIEWED: 2026-07-17
 */
import React, { useState, useEffect } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ConfidenceBadge } from "../atoms";

const SignalCard = ({ p, isActive, onEnter, onLeave, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect && onSelect(p)}
    onMouseEnter={() => onEnter && onEnter(p.id)}
    onMouseLeave={() => onLeave && onLeave()}
    onFocus={() => onEnter && onEnter(p.id)}
    onBlur={() => onLeave && onLeave()}
    className={`group w-full text-left rounded-[12px] border p-3.5 overflow-hidden transition-all duration-200 ${
      isActive
        ? "border-[var(--ink)] bg-[var(--surface-2)]"
        : "border-[var(--hairline)] bg-[var(--paper)] hover:border-[var(--hairline-strong)]"
    }`}
    data-testid={`signal-card-${p.id}`}
  >
    {/* Country + category pill + time */}
    <div className="flex items-start justify-between gap-2 min-w-0">
      <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--muted)] truncate min-w-0">
        {p.city}{p.country ? `, ${p.country}` : ""}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {p.category && (
          <span className="font-mono text-[9px] tracking-[0.1em] uppercase text-[var(--muted)] border border-[var(--hairline)] px-1.5 py-0.5 rounded">
            {p.category.replace(/-/g, " ")}
          </span>
        )}
        <span className="font-mono text-[10px] text-[var(--muted)] whitespace-nowrap">
          {p.timeAgo}
        </span>
      </div>
    </div>

    {/* Headline */}
    <div className="font-serif text-[14px] leading-[1.3] text-[var(--ink)] mt-1.5 line-clamp-3">
      {p.headline}
    </div>

    {/* Source name + signal count */}
    {p.source_name && (
      <div className="mt-1.5 font-mono text-[10px] text-[var(--muted)] truncate">
        {p.source_name}
        {p.sources && <span className="text-[var(--hairline-strong)]"> · </span>}
        {p.sources && <span>{p.sources}</span>}
      </div>
    )}

    {/* Confidence + Read CTA */}
    <div className="mt-2.5 pt-2.5 border-t border-[var(--hairline)] flex items-center gap-2 min-w-0">
      <ConfidenceBadge level={p.confidence} className="flex-1 min-w-0 overflow-hidden" />
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--ink-2)] group-hover:text-[var(--crimson)] whitespace-nowrap shrink-0 transition-colors">
        {p.story_id ? "Read story" : "Read"}
        <ArrowRight size={11} strokeWidth={2} />
      </span>
    </div>
  </button>
);

// Auto-scrolling live feed panel. Pauses on hover or when a map dot is externally hovered.
export const LiveFeedPanel = ({ items, activeId, onEnter, onLeave, onSelect }) => {
  const scrollRef = React.useRef(null);
  const [paused, setPaused] = useState(false);
  const externalHover = activeId !== null; // map dot is being hovered → pause + sync

  // Auto-scroll — stops when panel is hovered OR a map dot is hovered
  useEffect(() => {
    if (paused || externalHover) return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 1) el.scrollTop = 1;
    const id = setInterval(() => {
      if (!el) return;
      const half = el.scrollHeight - el.clientHeight;
      if (half <= 0) return;
      let next = el.scrollTop + 0.6;
      if (next >= half) next = next - half;
      el.scrollTop = next;
    }, 16);
    return () => clearInterval(id);
  }, [paused, externalHover]);

  // Scroll to the hovered card when activeId changes from map interaction
  useEffect(() => {
    if (!activeId || !scrollRef.current) return;
    const container = scrollRef.current;
    const card = container.querySelector(`[data-testid="signal-card-${activeId}"]`);
    if (!card) return;
    // Only snap if card is not already fully in view
    const cardTop = card.offsetTop;
    const cardBottom = cardTop + card.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    if (cardTop >= viewTop && cardBottom <= viewBottom) return;
    // Center the card in the panel (instant, no smooth animation to avoid fighting auto-scroll)
    container.scrollTop = Math.max(0, cardTop - (container.clientHeight / 2) + (card.offsetHeight / 2));
  }, [activeId]);

  return (
    <div
      ref={scrollRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="absolute inset-0 overflow-y-auto dnh-no-scrollbar"
      data-testid="live-feed-scroll"
    >
      <div className="flex flex-col gap-2.5 pr-1">
        {items.map((p, i) => (
          <SignalCard
            key={`${p.id}-${i}`}
            p={p}
            isActive={activeId === p.id}
            onEnter={onEnter}
            onLeave={onLeave}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};

/* ─────────── Live Intelligence Carousel (Issue 3) ─────────── */
export const LiveIntelligenceSection = ({ stories = [], country }) => {
  const PAGE_SIZE = 6;
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(stories.length / PAGE_SIZE);
  const pageStories = stories.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => { setPage(0); }, [stories]);

  if (!stories.length) return null;

  return (
    <section className="py-12 border-t border-[var(--hairline)]" data-testid="live-intelligence-section">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        {/* Header row */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[var(--crimson)] animate-pulse" />
            <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--crimson)]">
              Live · {country ? `${country.name} Intelligence` : "Global Intelligence"}
            </span>
            <span className="font-mono text-[10px] text-[var(--muted)]">
              {stories.length} signals
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] tracking-[0.08em] text-[var(--muted)] mr-1">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="live-intel-prev"
                aria-label="Previous page"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--hairline)] text-[var(--ink)] transition-[opacity,border-color,color] duration-200 hover:border-[var(--crimson)] hover:text-[var(--crimson)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[var(--hairline)] disabled:hover:text-[var(--ink)]"
              >
                <ChevronLeft size={14} strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="live-intel-next"
                aria-label="Next page"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--hairline)] text-[var(--ink)] transition-[opacity,border-color,color] duration-200 hover:border-[var(--crimson)] hover:text-[var(--crimson)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[var(--hairline)] disabled:hover:text-[var(--ink)]"
              >
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        {/* 3-col grid — page-based */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pageStories.map((story) => (
            <article
              key={story.id}
              onClick={() => story.id && navigate(`/briefs/${story.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && story.id && navigate(`/briefs/${story.id}`)}
              className="border border-[var(--hairline)] rounded-[3px] p-5 hover:bg-[var(--surface-2)] transition-colors cursor-pointer"
              data-testid={`live-story-${story.id}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {story.source_count > 1 ? (
                  <span className="font-mono text-[9.5px] tracking-[0.12em] uppercase text-[var(--crimson)]">
                    {story.source_count} sources
                  </span>
                ) : (
                  <span className="font-mono text-[9.5px] tracking-[0.12em] uppercase text-[var(--muted)]">
                    {story.source_name || "DNH"}
                  </span>
                )}
                {story.category_slug && (
                  <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[var(--surface-2)] text-[var(--ink-2)] rounded-full capitalize">
                    {story.category_slug.replace(/-/g, " ")}
                  </span>
                )}
                {story.confidence && (
                  <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[var(--surface-2)] text-[var(--ink-2)] rounded-full">
                    {story.confidence}
                  </span>
                )}
              </div>
              <h3 className="font-serif text-[16px] sm:text-[17px] font-medium text-[var(--ink)] leading-[1.35] mb-2">
                {story.headline}
              </h3>
              {story.lens_implication ? (
                <div className="mt-2 p-2.5 bg-[#FEF3E2] rounded-[2px]">
                  <span className="font-mono text-[8.5px] tracking-[0.16em] uppercase text-[var(--gold)] block mb-1">
                    {country?.name} lens
                  </span>
                  <p className="font-sans text-[12px] text-[var(--ink)] leading-[1.55] font-medium">
                    {story.lens_implication}
                  </p>
                </div>
              ) : (
                <p className="font-sans text-[12.5px] text-[var(--ink-2)] leading-[1.6]">
                  {story.summary?.slice(0, 120)}{story.summary?.length > 120 ? "…" : ""}
                </p>
              )}
              {story.place && (
                <div className="mt-3 pt-2 border-t border-[var(--hairline)]">
                  <span className="font-mono text-[9.5px] text-[var(--muted)]">{story.place}</span>
                </div>
              )}
            </article>
          ))}
        </div>

        {/* Page dot indicator */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-6" data-testid="live-intel-dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i)}
                aria-label={`Page ${i + 1}`}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === page ? "bg-[var(--crimson)]" : "bg-[var(--hairline-strong)] hover:bg-[var(--muted)]"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
